/**
 * Onboarding Page
 *
 * Welcomes new users and collects their profile information before
 * allowing access to the main application. Investors get the four-step
 * flow in `InvestorOnboarding`; agents keep the single-card flow below.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { dynamicPath } from '@/common/utils/navigation'
import { Text } from '@/common/components/text'
import { useToast } from '@/common/components/toast'
import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { getDefaultRouteForRole } from '@/common/utils/getDefaultRouteForRole'
import {
  ProfileForm,
  type ProfileFormData,
} from '@/features/settings/components/ProfileForm'
import { ExternalAgentTermsStep } from '@/features/onboarding/components/ExternalAgentTermsStep/ExternalAgentTermsStep'
import { InvestorOnboarding } from '@/features/onboarding/components/InvestorOnboarding'

import * as styles from './onboarding.css'

export const Route = createFileRoute({
  component: OnboardingPage,
})

type OnboardingStep = 'profile' | 'extTerms' | 'training'

function OnboardingPage() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { redirect?: string }
  const { roles } = useAuth()
  const {
    data,
    updateProfile,
    completeOnboarding,
    acceptExternalAgentTerms,
    needsOnboarding,
    needsExternalAgentTerms,
    needsTraining,
  } = useUserProfile()
  const { showSuccessToast, showErrorToast } = useToast()
  const [isSaving, setIsSaving] = useState(false)
  const [isSigning, setIsSigning] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)

  const isInvestor = roles.includes('investor')
  const isExternalAgent = data?.profile?.agent_type === 'external'

  // Step state machine for agents — derived from server flags so the UI stays
  // in sync with the API after each successful mutation (updateProfile,
  // acceptExternalAgentTerms, completeTraining, etc.).
  const currentStep: OnboardingStep | 'done' = (() => {
    if (needsOnboarding) return 'profile'
    if (isExternalAgent) {
      if (needsExternalAgentTerms) return 'extTerms'
      if (needsTraining) return 'training'
    }
    return 'done'
  })()

  // Capture the redirect param once on mount to prevent it from being lost during re-renders.
  // This is important because the original redirect should be preserved through the entire onboarding flow.
  // Also prevent redirect loops by ignoring redirects back to /onboarding
  const [initialRedirect] = useState(() => {
    const redirect = search.redirect ?? null
    // Prevent redirect loops - don't redirect back to onboarding
    if (redirect?.startsWith('/onboarding')) {
      return null
    }
    return redirect
  })

  // Get intended destination after onboarding from captured param
  const redirectTo = initialRedirect || getDefaultRouteForRole(roles)

  // Redirect once the inline-rendered onboarding gates are clear. Profile and
  // external-agent ToS render inside this card. The training step (MRG-281)
  // is the exception — it's a guided tour over the real `/properties` routes
  // driven by `useAgentTrainingTour`, so we send the user to `/properties` and
  // let the global tour hook auto-launch. The server-side gate keeps blocking
  // real submissions until they finish. Investors redirect from
  // `InvestorOnboarding`, which holds them on its done step first.
  useEffect(() => {
    if (!isInvestor && data && !needsOnboarding && !needsExternalAgentTerms) {
      navigate({ to: dynamicPath(redirectTo), replace: true })
    }
  }, [isInvestor, data, needsOnboarding, needsExternalAgentTerms, navigate, redirectTo])

  // Agent name for type-to-sign guidance
  const agentName = data?.profile
    ? `${data.profile.first_name} ${data.profile.last_name}`.trim() || 'Your Name'
    : 'Your Name'

  /**
   * External-agent ToS sign: writes audit row + flips
   * `external_agent_terms_accepted_at`. After success, derived `currentStep`
   * advances to 'training' on the next render.
   */
  const handleExtAgentSign = async (fullLegalName: string, nameMismatchConfirmed = false) => {
    setIsSigning(true)
    setSignError(null)
    try {
      await acceptExternalAgentTerms(fullLegalName, nameMismatchConfirmed)
    } catch (err) {
      console.error('Failed to accept external-agent ToS:', err)
      setSignError('Failed to accept the Agreement. Please try again.')
      showErrorToast('Failed to accept the Agreement. Please try again.')
    } finally {
      setIsSigning(false)
    }
  }

  /**
   * Handle form submission - saves profile and completes onboarding.
   * Navigation is handled by the useEffect above once needsOnboarding becomes false.
   */
  const handleSubmit = async (formData: ProfileFormData) => {
    setIsSaving(true)
    try {
      await updateProfile({
        first_name: formData.first_name,
        last_name: formData.last_name,
        public_email: formData.public_email,
        public_phone: formData.public_phone,
        company_name: formData.company_name,
        markets: formData.markets,
      })

      // Then mark onboarding as complete - this updates state and triggers
      // the useEffect to navigate once React has processed the update
      await completeOnboarding()

      showSuccessToast('Welcome! Your profile has been set up.')
      // Navigation handled by useEffect when needsOnboarding becomes false
    } catch (err) {
      console.error('Failed to complete onboarding:', err)
      showErrorToast('Failed to save profile. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isInvestor) {
    return data ? <InvestorOnboarding redirectTo={redirectTo} /> : null
  }

  // Training step (MRG-281) is a guided tour over `/properties`, not an
  // inline onboarding card. The useEffect above navigates the agent there;
  // suppress the card render so they don't see an empty flash mid-redirect.
  if (currentStep === 'training') {
    return null
  }

  return (
    <div className={styles.container}>
      <div className={currentStep === 'extTerms' ? styles.cardWide : styles.card}>
        <div className={styles.header}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={styles.logo}
          />
          <Text as="h1" variant="h4" weight="bold" className={styles.title}>
            Welcome!
          </Text>
          <Text variant="body" className={styles.subtitle}>
            {currentStep === 'profile' && "Let's set up your profile to get started."}
            {currentStep === 'extTerms' &&
              'Review and sign the Marketplace Agent Agreement to continue.'}
          </Text>
        </div>

        {currentStep === 'profile' && (
          <>
            <ProfileForm
              initialValues={data?.profile}
              loginEmail={data?.email}
              userRole="agent"
              submitLabel="Get Started"
              isSubmitting={isSaving}
              allowPristineSubmit
              onSubmit={handleSubmit}
            />
            <div className={styles.footer}>
              <Text variant="bodySm" color="muted">
                You can update your profile anytime in Settings.
              </Text>
            </div>
          </>
        )}

        {currentStep === 'extTerms' && data?.profile && (
          <>
            <ExternalAgentTermsStep
              onSign={handleExtAgentSign}
              isSubmitting={isSigning}
              investorName={agentName}
            />
            {signError && (
              <Text variant="bodySm" style={{ color: '#d32f2f', textAlign: 'center', marginTop: 12 }}>
                {signError}
              </Text>
            )}
          </>
        )}

      </div>
    </div>
  )
}
